<script setup lang="ts">
import type { TooltipPlacement } from 'vidstack';

import Tooltip from '../Tooltip.vue';
import { localize } from '@/front/composables/StringComposer'

const { tooltipPlacement } = defineProps<{
  tooltipPlacement: TooltipPlacement;
}>();

import { PlayerStore } from '@/front/stores/player_store.js'
const playerStore = PlayerStore()

const enterFullscreen = async () => {
    log.info(1, 'enterFullscreen called');
    log.info(2, 'Current isFullscreen state:', playerStore.isFullscreen);
    
    const el = document.getElementById('player-wrapper');
    log.info(2, 'Player wrapper element:', el);
    
    if (!el) {
      log.error(1, 'Player wrapper element not found');
      return;
    }
    
    log.info(2, 'Available fullscreen APIs:');
    log.info(2, '- el.requestFullscreen:', typeof el.requestFullscreen);
    log.info(2, '- el.webkitRequestFullscreen:', typeof el.webkitRequestFullscreen);
    log.info(2, '- el.mozRequestFullScreen:', typeof el.mozRequestFullScreen);
    log.info(2, '- el.msRequestFullscreen:', typeof el.msRequestFullscreen);
    
    try {
      if (el.requestFullscreen) {
        log.info(1, 'Using standard requestFullscreen');
        await el.requestFullscreen();
        log.info(1, 'requestFullscreen completed successfully');
      } else if (el.webkitRequestFullscreen) {
        log.info(1, 'Using webkit requestFullscreen');
        await el.webkitRequestFullscreen();
        log.info(1, 'webkitRequestFullscreen completed successfully');
      } else if (el.mozRequestFullScreen) {
        log.info(1, 'Using moz requestFullScreen');
        await el.mozRequestFullScreen();
        log.info(1, 'mozRequestFullScreen completed successfully');
      } else if (el.msRequestFullscreen) {
        log.info(1, 'Using ms requestFullscreen');
        await el.msRequestFullscreen();
        log.info(1, 'msRequestFullscreen completed successfully');
      } else {
        log.error(1, 'No fullscreen API available on this device');
        log.info(1, 'User agent:', navigator.userAgent);
        return;
      }
      
      log.info(2, 'Setting isFullscreen to true');
      playerStore.isFullscreen = true;
      log.info(2, 'Fullscreen state updated');
    } catch (error) {
      log.error(1, 'Failed to enter fullscreen - Error details:', error);
      log.error(1, 'Error name:', error.name);
      log.error(1, 'Error message:', error.message);
      log.error(2, 'Error stack:', error.stack);
    }
};

const exitFullscreen = async () => {
    log.info(1, 'exitFullscreen called');
    log.info(2, 'Current isFullscreen state:', playerStore.isFullscreen);
    
    log.info(2, 'Available exit fullscreen APIs:');
    log.info(2, '- document.exitFullscreen:', typeof document.exitFullscreen);
    log.info(2, '- document.webkitExitFullscreen:', typeof document.webkitExitFullscreen);
    log.info(2, '- document.mozCancelFullScreen:', typeof document.mozCancelFullScreen);
    log.info(2, '- document.msExitFullscreen:', typeof document.msExitFullscreen);
    
    try {
      if (document.exitFullscreen) {
        log.info(1, 'Using standard exitFullscreen');
        await document.exitFullscreen();
        log.info(1, 'exitFullscreen completed successfully');
      } else if (document.webkitExitFullscreen) {
        log.info(1, 'Using webkit exitFullscreen');
        await document.webkitExitFullscreen();
        log.info(1, 'webkitExitFullscreen completed successfully');
      } else if (document.mozCancelFullScreen) {
        log.info(1, 'Using moz cancelFullScreen');
        await document.mozCancelFullScreen();
        log.info(1, 'mozCancelFullScreen completed successfully');
      } else if (document.msExitFullscreen) {
        log.info(1, 'Using ms exitFullscreen');
        await document.msExitFullscreen();
        log.info(1, 'msExitFullscreen completed successfully');
      } else {
        log.error(1, 'Exit fullscreen API not supported');
        log.info(1, 'User agent:', navigator.userAgent);
        return;
      }
      
      log.info(2, 'Setting isFullscreen to false');
      playerStore.isFullscreen = false;
      log.info(2, 'Fullscreen state updated');
    } catch (error) {
      log.error(1, 'Failed to exit fullscreen - Error details:', error);
      log.error(1, 'Error name:', error.name);
      log.error(1, 'Error message:', error.message);
      log.error(2, 'Error stack:', error.stack);
    }
};


const handleFullscreen = async () => {
  log.info(1, 'handleFullscreen clicked');
  log.info(1, 'Current playerStore.isFullscreen:', playerStore.isFullscreen);
  log.info(2, 'playerStore.playerState:', playerStore.playerState);
  log.info(2, 'Can fullscreen:', playerStore.playerState?.canFullscreen);
  log.info(2, 'playerState.fullscreen:', playerStore.playerState?.fullscreen);
  
  if (playerStore.isFullscreen) {
    log.info(1, 'Calling exitFullscreen');
    await exitFullscreen()
  } else {
    log.info(1, 'Calling enterFullscreen');
    await enterFullscreen()
  }
  
  log.info(1, 'handleFullscreen completed');
}

import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('FullscreenButton', { logLevel: 'none' })
} 
</script>

<template>
  <Tooltip :placement="tooltipPlacement">
    <template #trigger>
      <media-toggle-button
        @click.stop="handleFullscreen"
        @touchstart.stop="handleFullscreen"
        class="w-8 h-8 @lg/player:w-10 @lg/player:h-10 @2xl/player:w-12 @2xl/player:h-12 ring-media-focus group relative inline-flex cursor-pointer items-center justify-center rounded-md outline-none ring-inset hover:bg-white/20 data-[focus]:ring-4 aria-hidden:hidden"
      >
        <media-icon v-show="!playerStore.isFullscreen" type="fullscreen" />
        <media-icon v-show="playerStore.isFullscreen" type="fullscreen-exit" />
      </media-toggle-button>
    </template>

    <template #content>
      <!-- <span>{{ playerStore.isFullscreen ? localize('exit_fullscreen') : localize('enter_fullscreen') }}</span> -->
      <span v-show="!playerStore.isFullscreen">{{ localize('enter_fullscreen') }}</span>
      <span v-show="playerStore.isFullscreen">{{ localize('exit_fullscreen') }}</span>
    </template>
  </Tooltip>
</template>
